import React, { useState, useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, 
  Typography, Divider, Avatar, IconButton, Badge, AppBar, Toolbar, useMediaQuery, useTheme,
  Snackbar, Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import GavelIcon from '@mui/icons-material/Gavel';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from '../AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

const drawerWidth = 260;

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inactiveToastOpen, setInactiveToastOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (user && user.role === 'admin') {
      axios.get('/api/admin/stats')
        .then(res => {
          setPendingCount(res.data.pendingUsers + res.data.pendingBusinesses);
        })
        .catch(err => console.error("Errore nel caricare le statistiche per la notifica", err));
    }
  }, [user, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    axios.post('/api/activity/pageview', { path: location.pathname }).catch(() => {
      // Avoid blocking navigation if tracking fails
    });
  }, [location.pathname, user]);

  useEffect(() => {
    if (user?.role === 'user' && user?.availabilityStatus === 'non_disponibile') {
      setInactiveToastOpen(true);
    } else {
      setInactiveToastOpen(false);
    }
  }, [user?.role, user?.availabilityStatus]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', role: ['admin', 'user', 'business'] },
    { text: 'Opportunità', icon: <WorkIcon />, path: '/opportunita', role: ['user'] },
    { text: 'Opportunità Azienda', icon: <WorkIcon />, path: '/azienda/opportunita', role: ['business'] },
    { text: 'Aziende Partner', icon: <BusinessIcon />, path: '/businesses', role: ['admin', 'user'] },
    { text: 'Cerca Alumni', icon: <ManageSearchIcon />, path: '/search-alumni', role: ['business'] }
  ];

  const adminMenuItems = [
      { text: 'Gestione Utenti', icon: <PersonIcon />, path: '/admin/users' },
      { text: 'Gestione Aziende', icon: <ApartmentIcon />, path: '/admin/businesses' },
      { text: 'Ricerca Alumni', icon: <ManageSearchIcon />, path: '/admin/search-alumni' },
      { text: 'Opportunità Lavoro', icon: <WorkIcon />, path: '/admin/jobs' },
      { text: 'Gestione Guide', icon: <MenuBookIcon />, path: '/admin/guides' },
      { text: 'Configurazione', icon: <SettingsIcon />, path: '/admin-config' },
      { text: 'Insights', icon: <BarChartIcon />, path: '/admin/insights' }
  ];

  const superAdminMenuItems = [
      { text: 'Gestione Admin', icon: <SupervisorAccountIcon />, path: '/admin/admins' }
  ];
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!isMobile && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Box
            component="img"
            src="https://www.fondazioneminoprio.it/wp-content/uploads/2025/04/Logo-Fondazione-Minoprio-ITS-Academy-300x103.png"
            alt="Logo"
            sx={{ maxWidth: '160px', width: '100%', height: 'auto', mx: 'auto', display: 'block' }}
          />
        </Box>
      )}
      <Divider />
      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => {
          if (item.text === 'Dashboard') {
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton 
                  component={RouterLink} 
                  to={item.path} 
                  selected={location.pathname === item.path}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                >
                  <ListItemIcon>
                    <Badge color="error" variant="dot" invisible={pendingCount === 0}>
                      <DashboardIcon />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          }
          return (
            item.role.includes(user?.role) && (
              <ListItem key={item.text} disablePadding>
                <ListItemButton 
                  component={RouterLink} 
                  to={item.path} 
                  selected={location.pathname === item.path}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            )
          );
        })}
        
        {user && user.role === 'admin' && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>Amministrazione</Typography>
            {adminMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton 
                  component={RouterLink} 
                  to={item.path} 
                  selected={location.pathname === item.path}
                  onClick={isMobile ? handleDrawerToggle : undefined}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
            
            {user.isSuperAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>Super Admin</Typography>
                {superAdminMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton 
                      component={RouterLink} 
                      to={item.path} 
                      selected={location.pathname === item.path}
                      onClick={isMobile ? handleDrawerToggle : undefined}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
                <ListItem disablePadding>
                  <ListItemButton 
                    component={RouterLink} 
                    to="/admin/export" 
                    selected={location.pathname === '/admin/export'}
                    onClick={isMobile ? handleDrawerToggle : undefined}
                  >
                    <ListItemIcon><DownloadIcon /></ListItemIcon>
                    <ListItemText primary="Esporta Dati" />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </>
        )}
      </List>

      {user && user.role === 'user' && (
        <List sx={{ flexGrow: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to="/stato-notifiche" 
              selected={location.pathname === '/stato-notifiche'}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon><NotificationsIcon /></ListItemIcon>
              <ListItemText primary="Stato e Notifiche" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to="/guida" 
              selected={location.pathname === '/guida'}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon><MenuBookIcon /></ListItemIcon>
              <ListItemText primary="Guida" />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {user && user.role === 'business' && (
        <List sx={{ flexGrow: 0, pb: 1 }}>
          <ListItem disablePadding>
            <ListItemButton 
              component={RouterLink} 
              to="/guida" 
              selected={location.pathname === '/guida'}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon><MenuBookIcon /></ListItemIcon>
              <ListItemText primary="Guida" />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {/* Link legale in fondo, visibile a tutti i ruoli */}
      <List sx={{ flexGrow: 0, pb: 1 }}>
        <ListItem disablePadding>
          <ListItemButton 
            component={RouterLink} 
            to="/segnala-problema" 
            selected={location.pathname === '/segnala-problema'}
            onClick={isMobile ? handleDrawerToggle : undefined}
          >
            <ListItemIcon><ReportProblemIcon /></ListItemIcon>
            <ListItemText primary="Segnala un problema" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            component={RouterLink} 
            to="/privacy-terms" 
            selected={location.pathname === '/privacy-terms'}
            onClick={isMobile ? handleDrawerToggle : undefined}
          >
            <ListItemIcon><GavelIcon /></ListItemIcon>
            <ListItemText primary="Informazioni Legali" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, mr: 1.5 }}>
          {(user?.name || 'U').charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }} noWrap>{user?.name}</Typography>
          <Typography variant="caption" noWrap>{user?.role}</Typography>
        </Box>
		    <IconButton component={RouterLink} to="/profilo" title="Modifica Profilo" size="small">
   			 <EditIcon fontSize="small" />
  		  </IconButton>
        <IconButton onClick={handleLogout} title="Logout" size="small">
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar per mobile */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          sx={{ 
            bgcolor: 'white', 
            color: 'text.primary',
            boxShadow: 1,
            zIndex: (theme) => theme.zIndex.drawer + 1
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: 2 }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src="https://www.fondazioneminoprio.it/wp-content/uploads/2025/04/Logo-Fondazione-Minoprio-ITS-Academy-300x103.png"
                alt="Logo"
                sx={{ height: 28, width: 'auto' }}
              />
            </Box>
            <IconButton
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ color: 'primary.main' }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      
      {/* Drawer permanente desktop, temporaneo mobile */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            ...(isMobile && { top: 56, height: 'calc(100% - 56px)' })
          },
        }}
        anchor="left"
      >
        {drawerContent}
      </Drawer>
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          bgcolor: 'background.default', 
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '56px', md: 0 }
        }}
      >
        <Outlet />
      </Box>

      <Snackbar
        open={inactiveToastOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: isMobile ? 7 : 2, mr: 1 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setInactiveToastOpen(false)}
          sx={{ maxWidth: 460 }}
        >
          Il tuo profilo è attualmente impostato su "Non disponibile" e quindi non visibile alle aziende. Puoi tornare "Attivo" dalla sezione "Stato e Notifiche".
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;